import { TextField, type TextFieldProps } from '@shared/components';
import IMask, { type FactoryArg } from 'imask';
import { useEffect, useRef } from 'react';
import { useController, type FieldValues, type UseControllerProps } from 'react-hook-form';

export type MaskFormTextFieldProps<TFieldValues extends FieldValues> =
  UseControllerProps<TFieldValues> &
    TextFieldProps & {
      maskOptions: FactoryArg;
    };

export default function MaskFormTextField<TFieldValues extends FieldValues>(
  props: Readonly<MaskFormTextFieldProps<TFieldValues>>,
) {
  const { maskOptions, ...restProps } = props;

  const inputRef = useRef<HTMLInputElement | null>(null);

  const {
    field,
    fieldState: { error },
  } = useController({ ...props, name: props.name });

  useEffect(() => {
    if (maskOptions && inputRef.current) {
      const maskedInput = IMask(inputRef.current, maskOptions);

      const handleAccept = () => {
        field.onChange(maskedInput.value);
      };

      maskedInput.on('accept', handleAccept);

      return () => {
        maskedInput.off('accept', handleAccept);
        maskedInput.destroy();
      };
    }
  }, [maskOptions, field]);

  return (
    <TextField
      {...restProps}
      {...field}
      type={props?.type ?? 'text'}
      error={!!error}
      helperText={error?.message ?? props.helperText ?? null}
      required={Boolean(props.rules?.required ?? props.required)}
      inputRef={inputRef}
      {...restProps}
    />
  );
}
